import { apiClient } from './apiClient';

/**
 * Custom domains for a tenant.
 *
 * Adding a domain does not make it usable — it returns a token the tenant must
 * publish as a DNS TXT record. Only a successful verification makes the hostname
 * resolve to the tenant.
 */

export interface TenantDomain {
  id: string;
  host: string;
  kind: 'app' | 'widget' | 'api';
  isPrimary: boolean;
  verificationToken: string | null;
  verifiedAt: string | null;
  lastCheckedAt: string | null;
  tlsStatus: string;
  isActive: boolean;
  createdAt: string;
}

export interface DomainListResponse {
  domains: TenantDomain[];
  instructions: {
    /** Label the TXT record goes under, e.g. "_konductors-verify". */
    verificationHost: string;
    /** What the host should CNAME to, when the platform advertises one. */
    cnameTarget: string | null;
  };
}

// Every call is super-admin only, and `authenticate` defaults to off — omitting
// it sends no Authorization header and the gateway rejects the request.
const auth = { authenticate: true };

export const domainService = {
  list(tenantId: string) {
    return apiClient.get<DomainListResponse>(`/auth/api/tenants/${tenantId}/domains`, auth);
  },

  add(tenantId: string, host: string, kind: TenantDomain['kind'] = 'app') {
    return apiClient.post<TenantDomain>(`/auth/api/tenants/${tenantId}/domains`, { host, kind }, auth);
  },

  verify(tenantId: string, domainId: string) {
    return apiClient.post<{ verified: boolean }>(
      `/auth/api/tenants/${tenantId}/domains/${domainId}/verify`,
      {},
      auth
    );
  },

  setPrimary(tenantId: string, domainId: string) {
    return apiClient.post<{ message: string }>(
      `/auth/api/tenants/${tenantId}/domains/${domainId}/primary`,
      {},
      auth
    );
  },

  remove(tenantId: string, domainId: string) {
    return apiClient.delete<{ message: string }>(`/auth/api/tenants/${tenantId}/domains/${domainId}`, auth);
  },
};
